import React, { useEffect, useState } from 'react';
import { OptionsMenuItem } from 'components/OptionsMenu/OptionsMenuItem';
import { t } from '@lingui/macro';
import { graphql, useFragment } from 'utils/graphClient';
import { useMutation } from 'utils/graphClient';
import { MarkAsOption_pinNodeMutation } from './__generated__/MarkAsOption_pinNodeMutation.graphql';
import { MarkAsOption_unpinNodeMutation } from './__generated__/MarkAsOption_unpinNodeMutation.graphql';
import { MarkAsOption_node$key } from './__generated__/MarkAsOption_node.graphql';
import { useFeatureFlag } from 'utils/sessionProvider';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@cardboard-ui/react';
import { createIcon } from 'components/icons/createIcon';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';

const pinAsAgendaQuery = graphql`
  mutation MarkAsOption_pinNodeMutation($input: PinningPinMutationInput!) {
    pin(input: $input) {
      nodePinned {
        __typename
        id
        ... on HasPinsInterface {
          pins {
            context
            id
            label
          }
        }
      }
    }
  }
`;

const unpinAsAgendaQuery = graphql`
  mutation MarkAsOption_unpinNodeMutation($input: PinningUnpinMutationInput!) {
    unpin(input: $input) {
      nodeUnpinned {
        __typename
        id
        ... on HasPinsInterface {
          pins {
            context
            id
            label
          }
        }
      }
    }
  }
`;

const dataQuery = graphql`
  fragment MarkAsOption_node on Node {
    __typename
    id
    ... on HasPinsInterface {
      pins {
        context
        id
        label
      }
    }
    ... on HasOwnerPermissionsInterface {
      ownershipEvent {
        id
      }
    }
  }
`;

interface MarkAsAgendaOptionProps {
  node: MarkAsOption_node$key;
  connectionId?: string;
}

type KnownPinLabels = 'AGENDA' | 'MINUTES';

const BookmarkIcon = createIcon(light('bookmark'));

export const MarkAsOption = ({ node: _node }: MarkAsAgendaOptionProps) => {
  const [pin] = useMutation<MarkAsOption_pinNodeMutation>(pinAsAgendaQuery);
  const [unpin] =
    useMutation<MarkAsOption_unpinNodeMutation>(unpinAsAgendaQuery);

  const pinsEnabled = useFeatureFlag(
    'FEATURE_EVENT_AGENDA_AND_MINUTES_AS_PINS',
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    id: itemId,
    pins,
    __typename,
    ownershipEvent,
  } = useFragment(dataQuery, _node);

  const agendaPinId = pins?.find((pin) => pin.label === 'AGENDA')?.id;
  const minutesPinId = pins?.find((pin) => pin.label === 'MINUTES')?.id;
  const parentEventId = ownershipEvent?.id;

  const originalMarkedAsAgenda = Boolean(agendaPinId);
  const originalMarkedAsMinutes = Boolean(minutesPinId);

  const [markedAsAgendaInModal, setMarkedAsAgendaInModal] = useState(
    originalMarkedAsAgenda,
  );
  const [markedAsMinutesInModal, setMarkedAsMinutesInModal] = useState(
    originalMarkedAsMinutes,
  );

  useEffect(() => {
    // reset when closing modal
    setMarkedAsAgendaInModal(originalMarkedAsAgenda);
    setMarkedAsMinutesInModal(originalMarkedAsMinutes);
  }, [isOpen]);

  if (
    __typename !== 'FolderItem' ||
    !parentEventId ||
    !pinsEnabled ||
    !itemId
  ) {
    return null;
  }

  // Submission has to be delayed because DOM structure changes while (un)pinning as agenda/minutes
  // This results in undesired side-effect that the modal starts freaking out.
  const onSubmit = ({
    markedAsAgenda,
    markedAsMinutes,
  }: {
    markedAsAgenda: boolean;
    markedAsMinutes: boolean;
  }) => {
    if (markedAsAgenda !== originalMarkedAsAgenda) {
      pinAs('AGENDA', agendaPinId);
    }

    if (markedAsMinutes !== originalMarkedAsMinutes) {
      pinAs('MINUTES', minutesPinId);
    }
  };

  const pinAs = (label: KnownPinLabels, pinId: string | undefined) => {
    if (pinId) {
      unpin({
        variables: {
          input: {
            id: pinId,
          },
        },
      });
    } else {
      pin({
        variables: {
          input: {
            nodeId: itemId,
            label,
            context: `CALENDAR_EVENT$${parentEventId}`,
          },
        },
      });
    }
  };

  return (
    <>
      <OptionsMenuItem onClick={onOpen} icon={<BookmarkIcon />}>
        {t`Mark as...`}
      </OptionsMenuItem>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t`Mark as`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CheckboxGroup>
              <Box py={2}>
                <Checkbox
                  isChecked={markedAsAgendaInModal}
                  onChange={(event) => {
                    setMarkedAsAgendaInModal(event.target.checked);
                  }}
                >{t`Agenda`}</Checkbox>
              </Box>
              <Box py={2}>
                <Checkbox
                  isChecked={markedAsMinutesInModal}
                  onChange={(event) => {
                    setMarkedAsMinutesInModal(event.target.checked);
                  }}
                >{t`Minutes`}</Checkbox>
              </Box>
            </CheckboxGroup>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                onSubmit({
                  markedAsAgenda: markedAsAgendaInModal,
                  markedAsMinutes: markedAsMinutesInModal,
                });
                onClose();
              }}
            >
              {t`Save`}
            </Button>
            <Button mr={3} onClick={onClose}>
              {t`Close`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
